import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { useTheme } from '../context/ThemeContext';

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const token = params.get('token') || '';
  const email = params.get('email') || '';
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    if (!tenantSlug) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(apiUrl(`/api/tenants/by-slug/${encodeURIComponent(tenantSlug)}`));
        if (active) setTenantInfo(res.data || null);
      } catch { if (active) setTenantInfo(null); }
    })();
    return () => { active = false; };
  }, [tenantSlug]);

  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'expired'
  const [message, setMessage] = useState('Checking your verification link…');
  const [resending, setResending] = useState(false);
  const [resentMessage, setResentMessage] = useState('');

  useEffect(() => {
    if (!token || !email) {
      setStatus('error');
      setMessage('This verification link is missing required information.');
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await axios.post(apiUrl('/api/auth/verify-email'), { email, token });
        if (!active) return;
        setStatus('success');
        setMessage(res.data?.message || 'Email verified. You can now sign in.');
        const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';
        setTimeout(() => navigate(loginPath, { replace: true }), 2500);
      } catch (err) {
        if (!active) return;
        const data = err.response?.data || {};
        if (data.expired) {
          setStatus('expired');
          setMessage(data.error || 'Verification link has expired.');
        } else {
          setStatus('error');
          setMessage(data.error || 'Could not verify this link.');
        }
      }
    })();
    return () => { active = false; };
  }, [token, email, navigate]);

  const handleResend = async () => {
    if (!email) return;
    setResending(true);
    setResentMessage('');
    try {
      const res = await axios.post(apiUrl('/api/auth/resend-verification'), { email });
      setResentMessage(res.data?.message || 'If this email is registered, a new link has been sent.');
    } catch (err) {
      setResentMessage(err.response?.data?.error || 'Failed to resend.');
    } finally {
      setResending(false);
    }
  };

  const tone =
    status === 'success' ? { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.4)', text: '#065f46' }
    : status === 'verifying' ? { bg: 'rgba(99,102,241,0.10)', border: 'rgba(99,102,241,0.3)', text: '#3730a3' }
    : { bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.3)', text: '#991b1b' };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #060c1a 0%, #0d1f3c 30%, #162447 55%, #1a1a5e 80%, #2d1b69 100%)' }}
    >
      <div className="w-full max-w-[460px] relative z-10">
        <div
          className="rounded-3xl overflow-hidden p-8"
          style={{
            background: isDark ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.985)',
            boxShadow: '0 40px 80px -16px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)',
          }}
        >
          <div className="h-[3px] -mx-8 -mt-8 mb-6" style={{ background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 40%, #8b5cf6 70%, #a855f7 100%)' }} />

          <h1
            className="text-2xl font-bold tracking-tight text-center mb-4"
            style={{
              background: isDark ? 'linear-gradient(135deg, #93c5fd, #818cf8)' : 'linear-gradient(135deg, #1e3a8a, #4f46e5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {status === 'success' ? 'Email verified' :
             status === 'expired' ? 'Link expired' :
             status === 'error' ? 'Verification failed' :
             'Verifying email…'}
          </h1>

          {tenantInfo?.companyName && (
            <p className="text-center text-xs font-semibold tracking-wide text-indigo-700 dark:text-indigo-300 mb-1">
              {tenantInfo.companyName}
            </p>
          )}
          {email && (
            <p className="text-center text-sm text-gray-500 dark:text-slate-400 mb-4 break-all">{email}</p>
          )}

          <div
            className="rounded-xl px-4 py-3 text-sm font-medium border text-center"
            style={{ background: tone.bg, borderColor: tone.border, color: isDark ? '#fff' : tone.text }}
          >
            {status === 'verifying' && (
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {message}
              </span>
            )}
            {status !== 'verifying' && message}
          </div>

          {status === 'success' && (
            <p className="mt-4 text-center text-xs text-gray-500 dark:text-slate-400">
              Redirecting to sign in…
            </p>
          )}

          {(status === 'expired' || status === 'error') && email && (
            <div className="mt-5 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-60"
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </button>
              {resentMessage && (
                <span className="text-xs text-gray-500 dark:text-slate-400 text-center">{resentMessage}</span>
              )}
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to={tenantSlug ? `/${tenantSlug}/login` : '/login'}
              className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
